import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useState } from "react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import { useTranslation } from "react-i18next";
import ImageWithFallback from "../ImageWithFallback";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "500px",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
  p: 4,
  maxHeight: "90vh",
  overflow: "auto",
};

export default function AddProductModal({
  open,
  edit,
  handleSave,
  setNewItem,
  handleClose,
  handleChange,
  handleCategories3Change,
  handleCategories4Change,
  handleSubmit,
  newItem,
  metadataInput,
  categories1Options,
  categories2Options,
  categories3Options,
  categories4Options,
  error,
}) {
  const [imagePreviews, setImagePreviews] = useState([]);
  const { t } = useTranslation();
  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    const newImagePreviews = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewItem((prevItem) => ({
          ...prevItem,
          productImages: [...(prevItem.productImages || []), reader.result],
        }));
      };
      reader.onloadend = () => {
        newImagePreviews.push(reader.result);
        if (newImagePreviews.length === files.length) {
          setImagePreviews((prevPreviews) => [
            ...prevPreviews,
            ...newImagePreviews,
          ]);
        }
      };
      reader.readAsDataURL(file);
    });
  };
  return (
    <Modal
      disableBackdropClick
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography id="modal-modal-title" variant="h4" component="h2">
          {!edit ? t("add_new_product") : t("edit_product")}
        </Typography>
        <hr></hr>
        <input
          accept="image/*"
          id="product-image-input"
          type="file"
          onChange={handleImageChange}
          style={{ display: "none" }}
          required
          multiple
        />
        <label htmlFor="product-image-input">
          <Button
            variant="contained"
            component="span"
            style={{ backgroundColor: "var(--orange)", marginBottom: "10px" }}
          >
            {t("upload_image")}
          </Button>
        </label>
        {newItem.productImages && newItem.productImages.length > 0 && (
          <ImageList cols={2} rowHeight={150}>
            {newItem.productImages.map((image, index) => (
              <ImageListItem key={index}>
                <Zoom>
                  <ImageWithFallback
                    loading="lazy"
                    src={image}
                    alt={`Product Preview ${index + 1}`}
                    style={{
                      width: "150px",
                      height: "auto",
                      maxHeight: "150px",
                      objectFit: "cover",
                    }}
                  />
                </Zoom>
              </ImageListItem>
            ))}
          </ImageList>
        )}
        <TextField
          error={error.productName}
          margin="dense"
          name="productName"
          label={t("product_name")}
          type="text"
          fullWidth
          value={newItem.productName}
          onChange={handleChange}
          required
        />
        <TextField
          error={error.brand}
          margin="dense"
          name="brand"
          label={t("brand")}
          type="text"
          fullWidth
          value={newItem.brand}
          onChange={handleChange}
          required
        />
        <FormControl fullWidth margin="dense">
          <InputLabel required id="categories1-label">
            {t("categories_lv1")}
          </InputLabel>
          <Select
            error={error.categories1}
            labelId="categories1-label"
            name="categories1"
            value={newItem.categories1}
            onChange={handleChange}
            label="Categories 1"
          >
            {categories1Options.map((option, index) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel required id="categories2-label">
            {t("categories_lv2")}
          </InputLabel>
          <Select
            labelId="categories2-label"
            name="categories2"
            value={newItem.categories2}
            onChange={handleChange}
            label={t("categories_lv2")}
          >
            {categories2Options.map((option, index) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          id="categories3"
          options={categories3Options}
          getOptionLabel={(option) => option}
          value={newItem.categories3}
          onChange={handleCategories3Change}
          renderInput={(params) => (
            <TextField
              error={error.categories3}
              margin="dense"
              {...params}
              variant="outlined"
              label={t("categories_lv3")}
              required
            />
          )}
        />
        <Autocomplete
          multiple
          freeSolo
          id="categories4"
          options={categories4Options}
          getOptionLabel={(option) => option}
          value={newItem.categories4}
          onChange={handleCategories4Change}
          onInput={(event) => {
            handleCategories4Change(event, "");
          }}
          renderInput={(params) => (
            <TextField
              margin="dense"
              {...params}
              variant="outlined"
              label={t("categories_lv4")}
            />
          )}
        />
        {metadataInput.map((metadata, index) => (
          <TextField
            key={index}
            error={error[metadata.id]}
            margin="dense"
            name={metadata.id}
            label={metadata.attribute}
            type="text"
            fullWidth
            value={newItem[metadata.id]}
            onChange={handleChange}
            required
          />
        ))}

        <TextField
          error={error.quantity}
          margin="dense"
          name="quantity"
          label={t("quantity")}
          type="number"
          fullWidth
          value={newItem.quantity}
          onChange={handleChange}
          required
          inputProps={{ min: "1" }}
        />
        <TextField
          error={error.description}
          margin="dense"
          name="description"
          label={t("description")}
          rows={4}
          type="text"
          fullWidth
          multiline
          value={newItem.description}
          onChange={handleChange}
          required
        />
        <TextField
          error={error.price}
          margin="dense"
          name="price"
          label={t("price")}
          type="number"
          fullWidth
          value={newItem.price}
          onChange={handleChange}
          required
          inputProps={{ min: "1" }}
        />
        <Button onClick={!edit ? handleSubmit : handleSave} color="primary">
          {t("add")}
        </Button>
        <Button onClick={handleClose} color="secondary">
          {t("cancel")}
        </Button>
      </Box>
    </Modal>
  );
}
